import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, CalendarClock, GitBranch, Plus, Rocket, Siren } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { FlatTable } from "../components/flat-table";
import { EmptyState, PageHeader, SectionHeader } from "../components/page";
import {
  LatestReleaseLine,
  ScheduleReleaseDialog,
  ShipReleaseDialog,
  UrgentReleaseDialog,
} from "../components/releases/release-dialogs";
import {
  formatSchedule,
  ReleaseKindBadge,
  ReleaseStatusBadge,
  releaseTitle,
} from "../components/releases/release-meta";
import { TimeAgo } from "../components/time-ago";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useIsAdmin } from "../lib/permissions";
import { OPEN_RELEASE, type ReleaseView } from "../types";

export function ReleasesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const admin = useIsAdmin();
  const query = useQuery({
    queryKey: ["releases"],
    queryFn: () => api.releases({ limit: 100 }),
    refetchInterval: 15_000,
  });
  const releases = query.data?.releases ?? [];
  const open = releases.filter((release) => OPEN_RELEASE.has(release.status));
  const standard = open.find((release) => release.kind === "standard");
  const urgent = open.filter((release) => release.kind === "urgent");
  const scheduled = open
    .filter((release) => release.kind === "scheduled")
    .sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
  const history = releases
    .filter((release) => !OPEN_RELEASE.has(release.status))
    .sort((a, b) => (b.publishedAt ?? b.updatedAt).localeCompare(a.publishedAt ?? a.updatedAt));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["releases"] });
    void queryClient.invalidateQueries({ queryKey: ["latest-release"] });
  };
  const fail = (fallback: string) => (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : fallback);

  const [shipTarget, setShipTarget] = useState<ReleaseView | null>(null);
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const shipDetail = useQuery({
    queryKey: ["release", shipTarget?.id],
    queryFn: () => api.release(shipTarget!.id),
    enabled: Boolean(shipTarget),
  });
  const ship = useMutation({
    mutationFn: ({ id, tag }: { id: string; tag?: string }) => api.shipRelease(id, tag),
    onSuccess: (detail) => {
      toast.success(
        detail.release.status === "shipping"
          ? "Shipping: publishing once every issue is done"
          : `Published ${detail.release.tag ?? detail.release.name}`,
      );
      setShipTarget(null);
      refresh();
    },
    onError: fail("Could not ship the release"),
  });
  const create = useMutation({
    mutationFn: (input: Parameters<typeof api.createRelease>[0]) => api.createRelease(input),
    onSuccess: (detail) => {
      toast.success(`${detail.release.name} opened as a GitHub draft`);
      setUrgentOpen(false);
      setScheduleOpen(false);
      refresh();
      navigate(`/releases/${detail.release.id}`);
    },
    onError: fail("Could not create the release"),
  });

  return (
    <>
      <PageHeader
        title="Releases"
        description="GitHub is the system of record: every open release is a draft release there, shipping publishes it, and the monorepo deploys from the tag."
        actions={
          admin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus /> New release
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!standard && (
                  <DropdownMenuItem onClick={() => create.mutate({ kind: "standard" })}>
                    <Rocket /> Open the next standard release
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setUrgentOpen(true)}>
                  <Siren /> Urgent release…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setScheduleOpen(true)}>
                  <CalendarClock /> Scheduled release…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      <LatestReleaseLine />

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <section className="space-y-2">
            <SectionHeader title="Open" count={open.length || undefined} />
            {open.length === 0 ? (
              <EmptyState
                icon={Rocket}
                title="Nothing open"
                description="The next standard release opens on its own when an issue is done. Promote an issue from its sidebar for an urgent or scheduled release."
              />
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                {standard && (
                  <OpenReleaseCard
                    release={standard}
                    onOpen={() => navigate(`/releases/${standard.id}`)}
                    onShip={admin ? () => setShipTarget(standard) : undefined}
                  />
                )}
                {urgent.map((release) => (
                  <OpenReleaseCard
                    key={release.id}
                    release={release}
                    onOpen={() => navigate(`/releases/${release.id}`)}
                    onShip={admin ? () => setShipTarget(release) : undefined}
                  />
                ))}
                {scheduled.map((release) => (
                  <OpenReleaseCard
                    key={release.id}
                    release={release}
                    onOpen={() => navigate(`/releases/${release.id}`)}
                    onShip={admin ? () => setShipTarget(release) : undefined}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <SectionHeader title="History" count={history.length || undefined} />
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No release has shipped through Skynet yet.</p>
            ) : (
              <FlatTable>
                <TableHeader>
                  <TableRow>
                    <TableHead>Release</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Issues</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((release) => (
                    <TableRow
                      key={release.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/releases/${release.id}`)}
                    >
                      <TableCell className="font-mono">{releaseTitle(release)}</TableCell>
                      <TableCell>
                        <ReleaseKindBadge kind={release.kind} />
                      </TableCell>
                      <TableCell>
                        <ReleaseStatusBadge status={release.status} />
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {release.issueCount}
                        {release.untrackedCount ? (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            +{release.untrackedCount} not tracked
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {release.publishedAt ? <TimeAgo at={release.publishedAt} /> : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {release.githubUrl && (
                          <a
                            href={release.githubUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            GitHub <ArrowUpRight className="size-3" />
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </FlatTable>
            )}
          </section>
        </>
      )}

      {shipTarget && (
        <ShipReleaseDialog
          open
          onOpenChange={(value) => !value && setShipTarget(null)}
          release={shipTarget}
          suggestedTag={shipDetail.data?.suggestedTag ?? null}
          pending={ship.isPending}
          onConfirm={(tag) => ship.mutate({ id: shipTarget.id, tag })}
        />
      )}
      <UrgentReleaseDialog
        open={urgentOpen}
        onOpenChange={setUrgentOpen}
        defaultFromLastRelease={false}
        pending={create.isPending}
        onConfirm={({ fromLastRelease }) => create.mutate({ kind: "urgent", fromLastRelease })}
      />
      <ScheduleReleaseDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        scheduled={scheduled}
        pending={create.isPending}
        onConfirm={(choice) =>
          "releaseId" in choice
            ? navigate(`/releases/${choice.releaseId}`)
            : create.mutate({ kind: "scheduled", scheduledFor: choice.scheduledFor })
        }
      />
    </>
  );
}

function OpenReleaseCard({
  release,
  onOpen,
  onShip,
}: {
  release: ReleaseView;
  onOpen: () => void;
  /** Absent for anyone who cannot ship. */
  onShip?: () => void;
}) {
  const hotfixBranch = release.branch.startsWith("release/");
  return (
    <Card className="gap-3 py-4 transition-[transform,box-shadow] duration-200 ease-calm hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none">
      <CardHeader className="px-4">
        <div className="flex flex-wrap items-center gap-2">
          <ReleaseKindBadge kind={release.kind} />
          <ReleaseStatusBadge status={release.status} />
          {release.kind === "scheduled" && release.scheduledFor && (
            <span className="text-xs text-muted-foreground">
              ships {formatSchedule(release.scheduledFor)}
            </span>
          )}
        </div>
        <CardTitle className="flex items-center gap-2 text-base">
          <button type="button" className="text-left hover:underline" onClick={onOpen}>
            {release.name}
          </button>
          {release.tag && release.tag !== release.name && (
            <span className="font-mono text-xs text-muted-foreground">→ {release.tag}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-4">
        <p className="text-sm text-muted-foreground">
          {release.issueCount} issue{release.issueCount === 1 ? "" : "s"}
          {release.untrackedCount ? ` · ${release.untrackedCount} not tracked in Skynet` : ""}
          {hotfixBranch && (
            <>
              {" "}
              · <GitBranch className="inline size-3" /> <span className="font-mono">{release.branch}</span>
              {release.baseTag ? ` from ${release.baseTag}` : ""}
            </>
          )}
          {release.status === "shipping" && " · waiting for the last issues to merge"}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onOpen}>
            Open
          </Button>
          {release.status === "open" && onShip && (
            <Button size="sm" onClick={onShip} disabled={release.issueCount === 0}>
              <Rocket /> Ship now
            </Button>
          )}
          {release.githubUrl && (
            <Button size="sm" variant="ghost" asChild>
              <a href={release.githubUrl} target="_blank" rel="noreferrer">
                Draft in GitHub <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
